import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon, CheckCircle, Truck, DollarSign, MapPin, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [pickupDate, setPickupDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    rentalType: '',
    projectType: '',
    notes: ''
  });

  const rentalOptions = [
    { value: '4hour', label: '4 Hour Rental', price: '$200', description: 'Perfect for quick cleanups' },
    { value: '1day', label: '1 Day Rental', price: '$300', description: 'Most popular option' },
    { value: '1week', label: '1 Week Rental', price: '$400', description: 'Best value for larger projects' }
  ];

  // Check for payment return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (payment === 'success' && sessionId) {
      setStep(5); // Payment success step
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'cancelled') {
      setStep(4); // Go back to payment step
      setPaymentStatus('cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRentalTypeSelect = (value) => {
    setFormData(prev => ({ ...prev, rentalType: value }));
  };

  const calculatePrice = async () => {
    if (!formData.rentalType || !formData.zipCode) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/calculate-price?rental_type=${formData.rentalType}&zip_code=${formData.zipCode}`
      );
      setPricing(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
    setLoading(false);
  };

  // Calculate price when rental type or zip changes
  useEffect(() => {
    if (formData.rentalType && formData.zipCode) {
      calculatePrice();
    }
  }, [formData.rentalType, formData.zipCode]);

  const handleCreateBooking = async () => {
    setLoading(true);
    try {
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        zip_code: formData.zipCode,
        rental_type: formData.rentalType,
        delivery_date: deliveryDate ? format(deliveryDate, 'yyyy-MM-dd') : '',
        pickup_date: pickupDate ? format(pickupDate, 'yyyy-MM-dd') : '',
        project_type: formData.projectType,
        notes: formData.notes
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingId(response.data.booking_id);
      setPricing(response.data.pricing);
      setStep(4); // Move to payment step
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Error creating booking. Please try again.');
    }
    setLoading(false);
  };

  const handlePayment = async () => {
    if (!bookingId) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Error processing payment. Please try again.');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setDeliveryDate(null);
    setPickupDate(null);
    setPricing(null);
    setBookingId(null);
    setPaymentStatus(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zipCode: '',
      rentalType: '',
      projectType: '',
      notes: ''
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center flex flex-col items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_cenwi-dumpsters/artifacts/as0yrnig_ChatGPT%20Image%20Jan%2023%2C%202026%2C%2002_46_35%20PM.png" 
              alt="Dump Trailer Dude"
              className="h-14 w-auto"
            />
            <span className="text-gray-700">Book Your Dump Trailer</span>
          </DialogTitle>
        </DialogHeader>

        {/* Step 5: Payment Success */}
        {step === 5 && (
          <div className="py-12 text-center">
            {paymentStatus === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-[#22C55E] mx-auto mb-6 animate-spin" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Payment...</h3>
                <p className="text-gray-600">Please wait while we confirm your payment.</p>
              </>
            )}
            {paymentStatus === 'success' && (
              <>
                <CheckCircle className="w-20 h-20 text-[#22C55E] mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Payment Successful!</h3>
                <p className="text-gray-600 mb-2">Thank you for choosing Dump Trailer Dude!</p>
                <p className="text-gray-600 mb-6">You'll receive a confirmation email shortly with your booking details.</p>
                <div className="bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                  <h4 className="font-semibold text-gray-900 mb-2">What's Next?</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• We'll call to confirm your delivery time</li>
                    <li>• Trailer delivered between 7:00 AM - 11:30 AM</li>
                    <li>• Tonnage fee ($70/ton) charged after pickup</li>
                  </ul>
                </div>
                <Button onClick={resetForm} className="bg-[#22C55E] hover:bg-[#16A34A] text-white">
                  Close
                </Button>
              </>
            )}
            {(paymentStatus === 'error' || paymentStatus === 'timeout') && (
              <>
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">⚠️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Status Unknown</h3>
                <p className="text-gray-600 mb-6">We couldn't confirm your payment status. Please check your email for confirmation.</p>
                <Button onClick={resetForm} className="bg-[#22C55E] hover:bg-[#16A34A] text-white">
                  Close
                </Button>
              </>
            )}
          </div>
        )}

        {/* Steps 1-4: Booking Form */}
        {step < 5 && (
          <div className="space-y-6 py-4">
            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3, 4].map((s, idx) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s ? 'bg-[#22C55E] text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {s}
                  </div>
                  {idx < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-[#22C55E]' : 'bg-gray-200'}`}></div>}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Contact Information */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(715) 555-1234"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    type="button" 
                    onClick={() => setStep(2)}
                    disabled={!formData.name || !formData.phone || !formData.email}
                    className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Delivery Location */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Delivery Location</h3>
                
                <div>
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main Street"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Enter your city"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zipCode">ZIP Code *</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="54401"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <p className="text-sm text-gray-500 flex items-center">
                  <MapPin className="w-4 h-4 mr-1 text-[#22C55E]" />
                  Servicing Central Wisconsin
                </p>

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => setStep(3)}
                    disabled={!formData.address || !formData.city || !formData.zipCode}
                    className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Rental Details */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Rental Details</h3>
                
                {/* Rental Type Selection */}
                <div>
                  <Label>Select Rental Duration *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                    {rentalOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => handleRentalTypeSelect(option.value)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.rentalType === option.value 
                            ? 'border-[#22C55E] bg-[#22C55E]/10' 
                            : 'border-gray-200 hover:border-[#22C55E]/50'
                        }`}
                      >
                        <div className="font-semibold text-gray-900">{option.label}</div>
                        <div className="text-[#22C55E] font-bold text-lg">{option.price}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Delivery Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {deliveryDate ? format(deliveryDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={deliveryDate}
                          onSelect={setDeliveryDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Pickup Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {pickupDate ? format(pickupDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={pickupDate}
                          onSelect={setPickupDate}
                          disabled={(date) => date < (deliveryDate || new Date())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label htmlFor="projectType">Project Type</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, projectType: value }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home-renovation">Home Renovation</SelectItem>
                      <SelectItem value="landscaping">Landscaping / Yard Work</SelectItem>
                      <SelectItem value="construction">Construction Debris</SelectItem>
                      <SelectItem value="cleanout">Home/Office Cleanout</SelectItem>
                      <SelectItem value="roofing">Roofing Project</SelectItem>
                      <SelectItem value="moving">Moving / Downsizing</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="notes">Special Instructions (Optional)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any special placement instructions, gate codes, or other details..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Price Preview */}
                {pricing && (
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#22C55E]" />
                      Price Estimate
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{pricing.package_name}</span>
                        <span className="font-medium">${pricing.flat_fee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mileage ({pricing.estimated_miles} mi roundtrip)</span>
                        <span className="font-medium">${pricing.mileage_fee.toFixed(2)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between font-bold">
                        <span>Total Due Today</span>
                        <span className="text-[#22C55E]">${pricing.total_upfront.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        + $70/ton disposal fee (charged after pickup)
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button 
                    type="button"
                    onClick={handleCreateBooking}
                    disabled={!deliveryDate || !pickupDate || !formData.rentalType || loading}
                    className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Continue to Payment
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Review & Pay</h3>
                
                {paymentStatus === 'cancelled' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                    Payment was cancelled. You can try again below.
                  </div>
                )}

                {/* Booking Summary */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#22C55E] mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">Delivery Address</div>
                      <div className="text-sm text-gray-600">{formData.address}</div>
                      <div className="text-sm text-gray-600">{formData.city}, WI {formData.zipCode}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#22C55E] mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">Rental Period</div>
                      <div className="text-sm text-gray-600">
                        {deliveryDate && format(deliveryDate, 'PPP')} - {pickupDate && format(pickupDate, 'PPP')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Final Price */}
                {pricing && (
                  <div className="bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Payment Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>{pricing.package_name}</span>
                        <span>${pricing.flat_fee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mileage Fee</span>
                        <span>${pricing.mileage_fee.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-[#22C55E]/30 pt-2 flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="text-[#22C55E]">${pricing.total_upfront.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500 text-center">
                  By clicking "Pay Now", you agree to our terms of service. 
                  Disposal fee ($70/ton) will be charged after trailer pickup.
                </div>

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(3)}>
                    Back
                  </Button>
                  <Button 
                    onClick={handlePayment}
                    disabled={loading}
                    className="bg-[#22C55E] hover:bg-[#16A34A] text-white px-8"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <DollarSign className="w-4 h-4 mr-2" />
                        Pay ${pricing?.total_upfront?.toFixed(2) || '0.00'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
